import React, { useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';
import { faqData } from '../data/faqData';

const FAQ: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    setActiveIndex(activeIndex === i ? null : i);
  };

  return (
    <section id="faq" style={{ background: '#1A1A1A', padding: 'clamp(60px, 8vw, 120px) 0' }}>
      <div className="container">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '80px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '32px' }}
        >
          <div>
            <span style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '5px',
              textTransform: 'uppercase' as const,
              color: '#C8A96E',
              display: 'block',
              marginBottom: '12px',
            }}>
              {lang === 'es' ? 'Preguntas frecuentes' : lang === 'en' ? 'Frequently asked questions' : 'Questions fréquentes'}
            </span>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(26px, 3.5vw, 48px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
              color: '#FFFFFF',
              maxWidth: '500px',
            }}>
              {lang === 'es' ? 'Todo lo que necesitas saber.' : lang === 'en' ? 'Everything you need to know.' : 'Tout ce que vous devez savoir.'}
            </h2>
          </div>
          <motion.a
            href="#contact"
            whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
            style={{
              padding: '14px 32px',
              border: '1px solid #C8A96E',
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '3px',
              textTransform: 'uppercase' as const,
              color: '#C8A96E',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap' as const,
              display: 'inline-block',
            }}
          >
            {lang === 'es' ? 'Hacer una pregunta' : lang === 'en' ? 'Ask a question' : 'Poser une question'}
          </motion.a>
        </motion.div>

        {/* FAQ Items */}
        <div style={{ maxWidth: '900px' }}>
          {faqData.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              style={{
                borderTop: i === 0 ? '1px solid rgba(200,169,110,0.15)' : 'none',
                borderBottom: '1px solid rgba(200,169,110,0.15)',
              }}
            >
              {/* Question row */}
              <button
                onClick={() => toggle(i)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  cursor: 'pointer', padding: '28px 0',
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: '24px', textAlign: 'left' as const,
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '17px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  lineHeight: 1.4,
                }}>
                  <span style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#C8A96E',
                    marginRight: '16px',
                    letterSpacing: '1px',
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {item.question[lang]}
                </span>

                {/* Plus / Minus icon */}
                <motion.div
                  animate={{ rotate: activeIndex === i ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: '32px', height: '32px', flexShrink: 0,
                    border: `1px solid ${activeIndex === i ? '#C8A96E' : 'rgba(200,169,110,0.2)'}`,
                    display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px', fontWeight: 300,
                    background: activeIndex === i ? '#C8A96E' : 'transparent',
                    transition: 'background 0.3s, border-color 0.3s',
                  }}
                >
                  <span style={{ color: activeIndex === i ? '#0D0D0D' : '#C8A96E', lineHeight: 1 }}>+</span>
                </motion.div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {activeIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{ paddingBottom: '28px', paddingLeft: '44px' }}>
                      <motion.p
                        initial={{ y: 10 }}
                        animate={{ y: 0 }}
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '16px',
                          color: '#AAAAAA',
                          lineHeight: 1.8,
                          maxWidth: '700px',
                          borderLeft: '3px solid #C8A96E',
                          paddingLeft: '20px',
                        }}
                      >
                        {item.answer[lang]}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FAQ;
